import { useEffect, useState } from 'react';
import { Menu, Zap } from 'lucide-react';
import { Button } from '@/components/ui/button';
import {
  Sheet,
  SheetContent,
  SheetHeader,
  SheetTitle,
  SheetTrigger,
} from '@/components/ui/sheet';
import { cn } from '@/lib/utils';

const NAV_LINKS = [
  { label: 'About', href: '#about' },
  { label: 'Tracks', href: '#tracks' },
  { label: 'Schedule', href: '#schedule' },
  { label: 'FAQ', href: '#faq' },
];

export function Logo({ className }: { className?: string }) {
  return (
    <a href="#top" className={cn('inline-flex items-center gap-2 group', className)}>
      <span className="grid size-9 place-items-center rounded-xl bg-accent text-primary shadow-sm transition-transform group-hover:rotate-6">
        <Zap className="size-5" fill="currentColor" aria-hidden />
      </span>
      <span className="font-display text-lg font-semibold tracking-tight">
        She<span className="text-primary">Sats</span>{' '}
        <span className="hidden min-[420px]:inline">Nairobi</span>
      </span>
    </a>
  );
}

export function SiteHeader() {
  const [scrolled, setScrolled] = useState(false);
  const [open, setOpen] = useState(false);

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 24);
    onScroll();
    window.addEventListener('scroll', onScroll, { passive: true });
    return () => window.removeEventListener('scroll', onScroll);
  }, []);

  return (
    <header
      className={cn(
        'fixed inset-x-0 top-0 z-50 transition-all duration-300',
        scrolled
          ? 'border-b border-border/60 bg-background/85 backdrop-blur-md shadow-sm'
          : 'border-b border-transparent bg-gradient-to-b from-black/40 to-transparent',
      )}
    >
      <div className="container flex h-16 items-center justify-between gap-4">
        <Logo
          className={cn(
            'transition-colors',
            scrolled ? 'text-foreground' : 'text-white',
          )}
        />

        <nav className="hidden items-center gap-1 md:flex" aria-label="Main">
          {NAV_LINKS.map((link) => (
            <a
              key={link.href}
              href={link.href}
              className={cn(
                'rounded-full px-4 py-2 text-sm font-medium transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring',
                scrolled
                  ? 'text-muted-foreground hover:bg-secondary hover:text-foreground'
                  : 'text-white/85 hover:bg-white/10 hover:text-white',
              )}
            >
              {link.label}
            </a>
          ))}
        </nav>

        <div className="flex items-center gap-2">
          <Button asChild className="hidden rounded-full font-semibold sm:inline-flex">
            <a href="#rsvp">Save your spot</a>
          </Button>

          <Sheet open={open} onOpenChange={setOpen}>
            <SheetTrigger asChild>
              <Button
                variant="ghost"
                size="icon"
                aria-label="Open menu"
                className={cn(
                  'md:hidden',
                  scrolled ? 'text-foreground' : 'text-white hover:bg-white/10 hover:text-white',
                )}
              >
                <Menu className="size-5" />
              </Button>
            </SheetTrigger>
            <SheetContent side="right" className="w-72">
              <SheetHeader>
                <SheetTitle className="text-left">
                  <Logo />
                </SheetTitle>
              </SheetHeader>
              <nav className="mt-6 flex flex-col gap-1" aria-label="Mobile">
                {NAV_LINKS.map((link) => (
                  <a
                    key={link.href}
                    href={link.href}
                    onClick={() => setOpen(false)}
                    className="rounded-lg px-4 py-3 text-base font-medium text-foreground transition-colors hover:bg-secondary"
                  >
                    {link.label}
                  </a>
                ))}
                <Button asChild className="mt-4 rounded-full font-semibold">
                  <a href="#rsvp" onClick={() => setOpen(false)}>
                    Save your spot
                  </a>
                </Button>
              </nav>
            </SheetContent>
          </Sheet>
        </div>
      </div>
    </header>
  );
}
